import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  Download,
  FileText,
  Users,
  Lock,
  Wifi,
  ArrowRight,
  Clock,
  Target,
  Award,
  Phone
} from 'lucide-react';

const CyberRisikoCheck = () => {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="min-h-screen py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Clean Header */}
        <div className="text-center space-y-6 mb-20">
          <Badge className="bg-red-100 text-red-800 text-sm px-4 py-2">
            <Shield className="mr-2 h-4 w-4" />
            Nach DIN SPEC 27076
          </Badge>
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900">
            <span className="text-red-600">Cyber</span>RisikoCheck
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Wie sicher ist Ihr Unternehmen wirklich? Finden Sie es heraus.
          </p>
          
          {/* Main CTA */}
          <div className="pt-8">
            <Button size="lg" className="bg-red-600 hover:bg-red-700 text-lg px-8 py-4" asChild>
              <Link to="/contact">
                Jetzt kostenlos analysieren lassen
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <p className="text-sm text-gray-500 mt-3">✓ Kostenlos ✓ Unverbindlich ✓ In 48h Ergebnis</p>
          </div>
        </div>

        {/* Simple Hero Visual */}
        <div className="mb-20">
          <div className="relative max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-red-600 to-blue-600 rounded-3xl p-12 text-center text-white">
              <div className="space-y-6">
                <Shield className="h-20 w-20 mx-auto opacity-90" />
                <h2 className="text-3xl font-bold">In nur 3 Schritten zur Sicherheit</h2>
                <div className="grid md:grid-cols-3 gap-8 mt-8">
                  <div className="space-y-3">
                    <div className="bg-white/20 rounded-full w-12 h-12 flex items-center justify-center mx-auto">
                      <span className="text-xl font-bold">1</span>
                    </div>
                    <h3 className="font-semibold">Kontakt</h3>
                    <p className="text-sm opacity-90">Sie rufen an oder schreiben</p>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white/20 rounded-full w-12 h-12 flex items-center justify-center mx-auto">
                      <span className="text-xl font-bold">2</span>
                    </div>
                    <h3 className="font-semibold">Analyse</h3>
                    <p className="text-sm opacity-90">Wir checken Ihre IT-Sicherheit</p>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white/20 rounded-full w-12 h-12 flex items-center justify-center mx-auto">
                      <span className="text-xl font-bold">3</span>
                    </div>
                    <h3 className="font-semibold">Bericht</h3>
                    <p className="text-sm opacity-90">Sie erhalten konkrete Empfehlungen</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hero Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          <div className="text-center">
            <div className="text-3xl font-bold text-red-600">78%</div>
            <div className="text-sm text-gray-600">der KMU wurden bereits angegriffen</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600">€186k</div>
            <div className="text-sm text-gray-600">durchschnittlicher Schaden</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">24h</div>
            <div className="text-sm text-gray-600">bis zum Ergebnis</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">100%</div>
            <div className="text-sm text-gray-600">kostenlose Erstanalyse</div>
          </div>
        </div>

        {/* What We Check - Simple Cards */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Das überprüfen wir für Sie
            </h2>
            <p className="text-lg text-gray-600">
              Professionelle Analyse Ihrer wichtigsten Sicherheitsbereiche
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="text-center p-6 hover:shadow-lg transition-shadow border-2 hover:border-red-200">
              <div className="mb-4">
                <Wifi className="h-12 w-12 text-red-600 mx-auto" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Netzwerk</h3>
              <p className="text-sm text-gray-600">Firewall, VPN, WLAN-Sicherheit</p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow border-2 hover:border-red-200">
              <div className="mb-4">
                <Lock className="h-12 w-12 text-red-600 mx-auto" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Datenschutz</h3>
              <p className="text-sm text-gray-600">DSGVO, Verschlüsselung, Backups</p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow border-2 hover:border-red-200">
              <div className="mb-4">
                <Users className="h-12 w-12 text-red-600 mx-auto" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Mitarbeiter</h3>
              <p className="text-sm text-gray-600">Schulungen, Passwörter, Phishing</p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow border-2 hover:border-red-200">
              <div className="mb-4">
                <Target className="h-12 w-12 text-red-600 mx-auto" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Compliance</h3>
              <p className="text-sm text-gray-600">BSI, ISO 27001, Normen</p>
            </Card>
          </div>
        </div>

        {/* Simple Stats */}
        <div className="mb-20 bg-gray-50 rounded-2xl p-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="space-y-3">
              <div className="text-4xl font-bold text-red-600">87%</div>
              <div className="text-sm text-gray-600">der deutschen KMU sind unzureichend geschützt</div>
            </div>
            <div className="space-y-3">
              <div className="text-4xl font-bold text-red-600">48h</div>
              <div className="text-sm text-gray-600">bis Sie Ihren Sicherheitsbericht erhalten</div>
            </div>
            <div className="space-y-3">
              <div className="text-4xl font-bold text-red-600">300+</div>
              <div className="text-sm text-gray-600">Unternehmen vertrauen bereits auf uns</div>
            </div>
          </div>
        </div>

        {/* Final Simple CTA */}
        <div className="text-center bg-red-600 rounded-2xl p-12 text-white">
          <h2 className="text-3xl font-bold mb-4">
            Sind Sie bereit für mehr Sicherheit?
          </h2>
          <p className="text-lg text-red-100 mb-8 max-w-xl mx-auto">
            Kontaktieren Sie uns für Ihren kostenlosen CyberRisikoCheck
          </p>
          <Button size="lg" variant="secondary" asChild>
            <Link to="/contact">
              Jetzt starten
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CyberRisikoCheck;